import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "text-sm text-gray-600 mb-4",
  "data-test": "intro"
}
const _hoisted_2 = {
  key: 0,
  class: "mb-4 space-y-2",
  "data-test": "warnings"
}
const _hoisted_3 = ["data-test"]
const _hoisted_4 = {
  key: 1,
  class: "text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2",
  "data-test": "drops-heading"
}
const _hoisted_5 = {
  key: 2,
  class: "divide-y divide-gray-200 border-y border-gray-200"
}
const _hoisted_6 = {
  key: 0,
  class: "ml-2 px-2 text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-700 whitespace-nowrap",
  "data-test": "current-drop"
}
const _hoisted_7 = {
  key: 1,
  class: "text-sm text-gray-500"
}
const _hoisted_8 = { class: "mt-2 space-y-1" }
const _hoisted_9 = { class: "text-gray-500" }
const _hoisted_10 = {
  key: 0,
  class: "text-gray-400"
}
const _hoisted_11 = {
  key: 0,
  class: "text-sm"
}
const _hoisted_12 = ["aria-expanded", "onClick"]
const _hoisted_13 = { class: "flex w-full justify-end gap-2" }

import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from '@heroicons/vue/20/solid'
import { computed, ref, watch } from 'vue'

import NeueButton from '@/components/neue/NeueButton.vue'
import TheModal from '@/components/TheModal.vue'
import { dropCountLabel, formatDropDay, formatReminderAt } from '@/helpers/dropImpact'

const ORDERS_SHOWN_IN_FULL = 3


export default {
  __name: 'DropsWithOrdersModal',
  props: {
  isOpen: { type: Boolean, default: false },
  // Only the affected drops that have orders on them; the rest need no listing.
  drops: { type: Array, default: () => [] },
  // Everything at stake across every affected drop, from the impact endpoint.
  totals: { type: Object, default: () => ({}) },
  // The drop the merchant is on, so it can be marked out in the list.
  currentDropId: { type: [String, Number], default: null },
  context: {
    type: String,
    default: 'delete',
    validator: value => ['delete', 'update'].includes(value)
  }
},
  emits: ['close', 'confirm'],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

const confirming = ref(false)

// A short list is worth showing whole; past this the drop collapses to its
// first order so the modal stays readable.
const expandedDropIds = ref([])

// Every open starts back at the first press, and with every list collapsed.
watch(
  () => props.isOpen,
  open => {
    if (open) return
    confirming.value = false
    expandedDropIds.value = []
  }
)

const isExpanded = drop => expandedDropIds.value.includes(drop.id)

const toggleOrders = drop => {
  expandedDropIds.value = isExpanded(drop)
    ? expandedDropIds.value.filter(id => id !== drop.id)
    : [...expandedDropIds.value, drop.id]
}

// The endpoint returns numeric ids and the route carries a string one. A drop
// id is never 0, so a falsy prop just means no drop to mark.
const isCurrentDrop = drop => Boolean(props.currentDropId) && String(drop.id) === String(props.currentDropId)

const allOrders = drop => drop.orders || []

const collapsible = drop => allOrders(drop).length > ORDERS_SHOWN_IN_FULL

const collapsed = drop => collapsible(drop) && !isExpanded(drop)

const visibleOrders = drop => (collapsed(drop) ? allOrders(drop).slice(0, 1) : allOrders(drop))

const hiddenCount = drop => allOrders(drop).length - visibleOrders(drop).length

const deleting = computed(() => props.context === 'delete')

const affectedCount = computed(() => props.totals.affected_drop_count || 0)

const orderCount = computed(() => props.totals.order_count || 0)

const many = computed(() => affectedCount.value > 1)

const dropsHeading = computed(
  () => `${dropCountLabel(props.drops.length)} with ${orderCount.value === 1 ? 'an order' : 'orders'}`
)

const count = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`

const title = computed(() => {
  if (deleting.value) {
    // affected_drop_count includes the drop being deleted.
    const others = affectedCount.value - 1
    return others > 0 ? `Delete this drop and ${count(others, 'future drop', 'future drops')}?` : 'Delete this drop?'
  }
  return `Update this drop and ${count(affectedCount.value, 'future drop', 'future drops')}?`
})

const intro = computed(() => {
  if (!deleting.value) {
    const one = orderCount.value === 1
    const subject = one ? 'The order below stays' : `The ${orderCount.value} orders below stay`
    return `Updating all future drops rebuilds them. ${subject} in Orders and ${
      one ? 'keeps the fulfillment slot it was' : 'keep the fulfillment slot they were'
    } placed for, but will no longer show on the drop itself.`
  }
  if (orderCount.value) {
    return `${count(
      orderCount.value,
      'customer has',
      'customers have'
    )} ordered. Their orders are not deleted - each one keeps the fulfillment slot it was placed for, so it still appears in Orders and on your production and packing lists.`
  }
  if (warnings.value.length) {
    return `${many.value ? 'These drops' : 'This drop'} will be switched off, along with everything still scheduled on ${
      many.value ? 'them' : 'it'
    }.`
  }
  return `Nobody has ordered on ${many.value ? 'these drops' : 'this drop'}, and nothing is scheduled on ${
    many.value ? 'them' : 'it'
  }.`
})

const warnings = computed(() => {
  const totals = props.totals
  const list = []

  if (!deleting.value) {
    if (totals.sent_reminder_count) {
      list.push({
        key: 'reminders-resent',
        severe: true,
        text: `${count(
          totals.sent_reminder_count,
          'reminder has',
          'reminders have'
        )} already gone out on these drops. Rebuilding schedules them again, so those customers may be reminded twice.`
      })
    }
    return list
  }

  if (totals.sent_reminder_count) {
    list.push({
      key: 'reminders-sent',
      severe: false,
      text: `${count(
        totals.sent_reminder_count,
        'reminder has',
        'reminders have'
      )} already gone out. Deleting will not recall ${totals.sent_reminder_count === 1 ? 'it' : 'them'}.`
    })
  }
  if (totals.pending_reminder_count) {
    const when = formatReminderAt(totals.next_reminder_at)
    list.push({
      key: 'reminders-cancelled',
      severe: true,
      text: `${count(totals.pending_reminder_count, 'reminder', 'reminders')}${
        when ? ` scheduled from ${when}` : ''
      } will not be sent.`
    })
  }
  if (totals.autocharge_at) {
    const unpaid = totals.unpaid_order_count
    list.push({
      key: 'autocharge-cancelled',
      severe: true,
      text: `Autocharge set for ${formatReminderAt(totals.autocharge_at)} will not run${
        unpaid ? ` - ${count(unpaid, 'customer', 'customers')} with an unpaid cart will not be charged` : ''
      }.`
    })
  }
  if (totals.autofill_pending) {
    list.push({ key: 'autofill-cancelled', severe: false, text: 'Autofill will not run.' })
  }
  return list
})

const confirmLabel = computed(() => {
  if (!deleting.value) return 'Update drops'
  return affectedCount.value > 1 ? 'Delete drops' : 'Delete drop'
})

const advance = () => {
  if (!confirming.value) {
    confirming.value = true
    return
  }
  confirming.value = false
  emit('confirm')
}

const close = () => {
  confirming.value = false
  emit('close')
}

return (_ctx, _cache) => {
  const _component_router_link = _resolveComponent("router-link")

  return (_openBlock(), _createBlock(TheModal, {
    "is-open": __props.isOpen,
    "modal-title": title.value,
    "footer-full-width": true,
    onCloseModal: close
  }, {
    main: _withCtx(() => [
      _createElementVNode("p", _hoisted_1, _toDisplayString(intro.value), 1),
      (warnings.value.length)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_2, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(warnings.value, (warning) => {
              return (_openBlock(), _createElementBlock("li", {
                key: warning.key,
                class: "flex gap-2 text-sm",
                "data-test": warning.key
              }, [
                _createVNode(_unref(ExclamationTriangleIcon), {
                  class: _normalizeClass(["h-4 w-4 shrink-0 mt-0.5", warning.severe ? 'text-red-500' : 'text-yellow-500']),
                  "aria-hidden": "true"
                }, null, 8, ["class"]),
                _createElementVNode("span", {
                  class: _normalizeClass(warning.severe ? 'text-gray-900 font-medium' : 'text-gray-600')
                }, _toDisplayString(warning.text), 3)
              ], 8, _hoisted_3))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (__props.drops.length)
        ? (_openBlock(), _createElementBlock("h4", _hoisted_4, _toDisplayString(dropsHeading.value), 1))
        : _createCommentVNode("", true),
      (__props.drops.length)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.drops, (drop) => {
              return (_openBlock(), _createElementBlock("li", {
                key: drop.id,
                class: "py-3",
                "data-test": "drop-with-orders"
              }, [
                _createVNode(_component_router_link, {
                  to: `/store/drops/${drop.id}`,
                  target: "_blank",
                  rel: "noopener noreferrer",
                  class: "inline-flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-blue-600"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(drop.description) + " ", 1),
                    _createVNode(_unref(ArrowTopRightOnSquareIcon), {
                      class: "h-3.5 w-3.5 shrink-0",
                      "aria-hidden": "true"
                    })
                  ]),
                  _: 2
                }, 1032, ["to"]),
                (isCurrentDrop(drop))
                  ? (_openBlock(), _createElementBlock("span", _hoisted_6, " This drop "))
                  : _createCommentVNode("", true),
                (drop.date)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_unref(formatDropDay)(drop.date)), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("ul", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleOrders(drop), (order) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: order.id,
                      class: "text-sm",
                      "data-test": "drop-order"
                    }, [
                      _createVNode(_component_router_link, {
                        to: `/order/${order.id}`,
                        target: "_blank",
                        rel: "noopener noreferrer",
                        class: "inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(" Order #" + _toDisplayString(order.id) + " ", 1),
                          _createVNode(_unref(ArrowTopRightOnSquareIcon), {
                            class: "h-3.5 w-3.5 shrink-0",
                            "aria-hidden": "true"
                          })
                        ]),
                        _: 2
                      }, 1032, ["to"]),
                      _createElementVNode("span", _hoisted_9, _toDisplayString(` - ${order.customer_name}`), 1),
                      (order.status)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(` (${order.status})`), 1))
                        : _createCommentVNode("", true)
                    ]))
                  }), 128)),
                  (collapsible(drop))
                    ? (_openBlock(), _createElementBlock("li", _hoisted_11, [
                        _createElementVNode("button", {
                          type: "button",
                          class: "text-blue-600 hover:text-blue-800 hover:underline",
                          "aria-expanded": isExpanded(drop),
                          "data-test": "toggle-orders",
                          onClick: $event => (toggleOrders(drop))
                        }, _toDisplayString(isExpanded(drop) ? 'Show less' : `+ ${hiddenCount(drop)} more`), 9, _hoisted_12)
                      ]))
                    : _createCommentVNode("", true)
                ])
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_13, [
        _createVNode(NeueButton, {
          type: "secondary",
          "data-test": "cancel",
          onClick: close
        }, {
          default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
            _createTextVNode("Cancel", -1)
          ]))]),
          _: 1
        }),
        _createVNode(NeueButton, {
          type: confirming.value ? 'danger' : 'red',
          "data-test": "confirm",
          onClick: advance
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(confirming.value ? "Yes, I'm sure" : confirmLabel.value), 1)
          ]),
          _: 1
        }, 8, ["type"])
      ])
    ]),
    _: 1
  }, 8, ["is-open", "modal-title"]))
}
}

}